import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Send, Plus, Trash2, RefreshCw, Mail, Globe, Info, ChevronDown, ChevronUp,
  CheckCircle2, XCircle, AlertTriangle,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL, COUNTRY_FLAGS } from './AdminShared';

const COUNTRIES = [
  { code: 'UK', label: 'United Kingdom' },
  { code: 'IN', label: 'India' },
  { code: 'CA', label: 'Canada' },
];

const EMPTY_CONTACT = { email: '', name: '', outlet: '', country: 'UK', city: '', audience: '', notes: '' };

export const AdminPressOutreach = () => {
  const { token } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [status, setStatus] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [filterCountry, setFilterCountry] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [newContact, setNewContact] = useState(EMPTY_CONTACT);
  const [showAdd, setShowAdd] = useState(false);
  const [showGuidance, setShowGuidance] = useState(false);
  const [guidance, setGuidance] = useState(null);

  const [sendModal, setSendModal] = useState(null); // { slug, title, country }
  const [testEmail, setTestEmail] = useState('');
  const [testOnly, setTestOnly] = useState(true);
  const [sendResult, setSendResult] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/seo/press-releases-status`, auth);
      setStatus(r.data);
    } catch {
      toast.error('Failed to load press status');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadContacts = useCallback(async () => {
    try {
      const url = filterCountry
        ? `${API_URL}/api/admin/press/contacts?country=${filterCountry}`
        : `${API_URL}/api/admin/press/contacts`;
      const r = await axios.get(url, auth);
      setContacts(r.data.items || []);
    } catch {
      toast.error('Failed to load contacts');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCountry]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadStatus(), loadContacts()]);
    setLoading(false);
  }, [loadStatus, loadContacts]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadGuidance = async () => {
    if (guidance) { setShowGuidance(s => !s); return; }
    try {
      const r = await axios.get(`${API_URL}/api/admin/press/outreach-guidance`, auth);
      setGuidance(r.data);
      setShowGuidance(true);
    } catch {
      toast.error('Failed to load guidance');
    }
  };

  const addContact = async () => {
    if (!newContact.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newContact.email)) {
      toast.error('Valid email required'); return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/press/contacts`, { contacts: [newContact] }, auth);
      if (r.data.inserted_count > 0) {
        toast.success(`Added ${r.data.inserted_count} contact`);
        setNewContact(EMPTY_CONTACT);
        setShowAdd(false);
        loadContacts();
      } else {
        const reason = r.data.rejected?.[0]?.reason || 'rejected';
        toast.error(`Not added: ${reason}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add');
    } finally { setBusy(false); }
  };

  const deleteContact = async (id, email) => {
    if (!window.confirm(`Remove ${email} from outreach list?`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/press/contacts/${id}`, auth);
      toast.success('Contact removed');
      loadContacts();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const openSend = (item) => {
    setSendModal({ slug: item.slug, title: item.city, country: 'UK' /* will refine after fetch */ });
    setTestOnly(true);
    setTestEmail('');
    setSendResult(null);
  };

  const performSend = async () => {
    if (!sendModal) return;
    if (testOnly && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmail)) {
      toast.error('Valid test email required'); return;
    }
    if (!testOnly) {
      const matching = contacts.filter(c => c.active !== false).length;
      if (!window.confirm(`Send "${sendModal.title}" press release to ALL ${matching} active matching journalists. Continue?`)) return;
    }
    setBusy(true);
    setSendResult(null);
    try {
      const body = testOnly ? { test_only: true, to: testEmail } : {};
      const r = await axios.post(`${API_URL}/api/admin/press/send/${sendModal.slug}`, body, auth);
      setSendResult(r.data);
      if (r.data.status === 'no_recipients') {
        toast.warning('No matching journalists for this country');
      } else {
        toast.success(`Sent to ${r.data.sent_count} (${r.data.failed_count} failed)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-[#E07A3A]" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="press-outreach-tab">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-['Playfair_Display'] text-[#1A1A1A]">Press Outreach</h2>
          <p className="text-sm text-slate-500 mt-1">
            Email AI-drafted press releases directly to journalists. Add real, verified contacts only.
          </p>
        </div>
        <button
          onClick={loadAll}
          className="px-3 py-2 text-xs uppercase tracking-wider border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-2"
          data-testid="press-refresh-btn"
        >
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      {/* Press release status */}
      <div className="bg-white border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-sm font-bold tracking-wider uppercase text-slate-900">
              City Press Releases — {status?.month}
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              {status?.published || 0} published · {status?.drafted || 0} drafted · {status?.total || 0} cities total
            </p>
          </div>
        </div>
        <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
          {(status?.items || []).map((item) => (
            <div key={item.slug} className="px-5 py-3 flex items-center justify-between gap-4 hover:bg-slate-50">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-900">{item.city}</span>
                  {item.published ? (
                    <span className="text-[10px] uppercase tracking-wider bg-emerald-950/95 text-emerald-300 border border-emerald-500/50 rounded px-2 py-0.5 font-bold">Published</span>
                  ) : item.exists ? (
                    <span className="text-[10px] uppercase tracking-wider bg-amber-950/95 text-amber-300 border border-amber-500/50 rounded px-2 py-0.5 font-bold">Draft</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-wider bg-slate-900 text-slate-300 border border-slate-500/40 rounded px-2 py-0.5 font-bold">Missing</span>
                  )}
                  {item.views > 0 && (
                    <span className="text-[10px] text-slate-400">· {item.views} views</span>
                  )}
                </div>
                <p className="text-xs text-slate-400 truncate mt-0.5 font-mono">{item.slug}</p>
              </div>
              <button
                disabled={!item.published}
                onClick={() => openSend(item)}
                className="px-3 py-1.5 text-xs uppercase tracking-wider bg-[#E07A3A] text-white hover:bg-[#C9682E] disabled:bg-slate-200 disabled:text-slate-400 inline-flex items-center gap-1.5"
                data-testid={`press-send-${item.slug}`}
              >
                <Send className="h-3 w-3" /> Send
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Outreach guidance */}
      <div className="bg-amber-50 border border-amber-200">
        <button
          onClick={loadGuidance}
          className="w-full px-5 py-3 flex items-center justify-between text-left"
          data-testid="press-guidance-toggle"
        >
          <div className="flex items-center gap-2">
            <Info className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-bold tracking-wider uppercase text-amber-900">Outreach Guidance & Press Desks</span>
          </div>
          {showGuidance ? <ChevronUp className="h-4 w-4 text-amber-600" /> : <ChevronDown className="h-4 w-4 text-amber-600" />}
        </button>
        {showGuidance && guidance && (
          <div className="px-5 py-4 border-t border-amber-200 text-sm text-amber-900 space-y-4">
            <p className="text-xs italic">{guidance.note}</p>
            {[
              ['UK property press', guidance.uk_property_press_examples],
              ['India property press', guidance.india_property_press_examples],
              ['Canada property press', guidance.canada_property_press_examples],
            ].map(([label, list]) => (
              <div key={label}>
                <p className="text-xs uppercase tracking-wider font-bold mb-1">{label}</p>
                <ul className="text-xs space-y-0.5 font-mono">
                  {list?.map(e => <li key={e}>· {e}</li>)}
                </ul>
              </div>
            ))}
            <div>
              <p className="text-xs uppercase tracking-wider font-bold mb-1">Best practices</p>
              <ul className="text-xs space-y-1 list-disc pl-5">
                {guidance.best_practices?.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Contacts manager */}
      <div className="bg-white border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-sm font-bold tracking-wider uppercase text-slate-900">Journalist Contacts</h3>
            <p className="text-xs text-slate-500 mt-1">{contacts.length} total · filter by country: {filterCountry || 'all'}</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={filterCountry}
              onChange={e => setFilterCountry(e.target.value)}
              className="text-xs border border-slate-300 px-2 py-1.5"
              data-testid="press-contact-filter"
            >
              <option value="">All countries</option>
              {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
            <button
              onClick={() => setShowAdd(s => !s)}
              className="px-3 py-2 text-xs uppercase tracking-wider bg-slate-900 text-white hover:bg-slate-700 inline-flex items-center gap-2"
              data-testid="press-add-contact-btn"
            >
              <Plus className="h-3 w-3" /> Add contact
            </button>
          </div>
        </div>

        {showAdd && (
          <div className="px-5 py-4 bg-slate-50 border-b border-slate-200 grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              type="email"
              placeholder="Email *"
              value={newContact.email}
              onChange={e => setNewContact({ ...newContact, email: e.target.value })}
              className="border border-slate-300 px-3 py-2 text-sm"
              data-testid="press-new-email"
            />
            <input
              placeholder="Journalist name"
              value={newContact.name}
              onChange={e => setNewContact({ ...newContact, name: e.target.value })}
              className="border border-slate-300 px-3 py-2 text-sm"
              data-testid="press-new-name"
            />
            <input
              placeholder="Outlet (e.g. The Times)"
              value={newContact.outlet}
              onChange={e => setNewContact({ ...newContact, outlet: e.target.value })}
              className="border border-slate-300 px-3 py-2 text-sm"
              data-testid="press-new-outlet"
            />
            <select
              value={newContact.country}
              onChange={e => setNewContact({ ...newContact, country: e.target.value })}
              className="border border-slate-300 px-3 py-2 text-sm"
              data-testid="press-new-country"
            >
              {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
            <input
              placeholder="City (optional)"
              value={newContact.city}
              onChange={e => setNewContact({ ...newContact, city: e.target.value })}
              className="border border-slate-300 px-3 py-2 text-sm"
            />
            <input
              placeholder="Audience focus (e.g. homeowners, investors)"
              value={newContact.audience}
              onChange={e => setNewContact({ ...newContact, audience: e.target.value })}
              className="border border-slate-300 px-3 py-2 text-sm"
            />
            <textarea
              placeholder="Notes (optional)"
              value={newContact.notes}
              onChange={e => setNewContact({ ...newContact, notes: e.target.value })}
              className="border border-slate-300 px-3 py-2 text-sm md:col-span-2"
              rows={2}
            />
            <div className="md:col-span-2 flex justify-end gap-2">
              <button
                onClick={() => { setShowAdd(false); setNewContact(EMPTY_CONTACT); }}
                className="px-3 py-2 text-xs uppercase tracking-wider border border-slate-300"
              >
                Cancel
              </button>
              <button
                disabled={busy}
                onClick={addContact}
                className="px-4 py-2 text-xs uppercase tracking-wider bg-[#E07A3A] text-white hover:bg-[#C9682E] disabled:opacity-50"
                data-testid="press-save-contact-btn"
              >
                {busy ? 'Saving...' : 'Save contact'}
              </button>
            </div>
          </div>
        )}

        <div className="divide-y divide-slate-100">
          {contacts.length === 0 ? (
            <div className="px-5 py-12 text-center text-sm text-slate-500">
              <Mail className="h-8 w-8 mx-auto mb-3 text-slate-300" />
              No journalist contacts yet. Add real, verified contacts only — never seed scraped lists.
            </div>
          ) : contacts.map((c) => (
            <div key={c.id} className="px-5 py-3 flex items-center justify-between gap-4 hover:bg-slate-50">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-slate-900">{c.email}</span>
                  {c.country && <span className="text-xs">{COUNTRY_FLAGS[c.country] || '🌐'} {c.country}</span>}
                  {c.outlet && <span className="text-xs text-slate-500">· {c.outlet}</span>}
                  {c.active === false && <span className="text-[10px] bg-slate-900 text-slate-300 border border-slate-500/40 rounded px-2 py-0.5 font-bold uppercase tracking-wider">Inactive</span>}
                </div>
                {(c.name || c.audience) && (
                  <p className="text-xs text-slate-500 truncate mt-0.5">
                    {c.name}{c.audience ? ` · ${c.audience}` : ''}
                  </p>
                )}
              </div>
              <button
                onClick={() => deleteContact(c.id, c.email)}
                className="p-1.5 text-slate-400 hover:text-red-600"
                data-testid={`press-delete-${c.id}`}
                title="Remove"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Send modal */}
      {sendModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => !busy && setSendModal(null)}>
          <div className="bg-white max-w-lg w-full p-6" onClick={e => e.stopPropagation()} data-testid="press-send-modal">
            <h3 className="text-lg font-bold text-slate-900 mb-1">Send press release</h3>
            <p className="text-xs text-slate-500 mb-4 font-mono">{sendModal.slug}</p>

            <div className="space-y-3 mb-4">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={testOnly}
                  onChange={() => setTestOnly(true)}
                  className="mt-1"
                  data-testid="press-mode-test"
                />
                <div>
                  <p className="text-sm font-medium text-slate-900">Test send</p>
                  <p className="text-xs text-slate-500">Send to a single email so you can preview formatting before broadcasting.</p>
                </div>
              </label>
              {testOnly && (
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={testEmail}
                  onChange={e => setTestEmail(e.target.value)}
                  className="w-full border border-slate-300 px-3 py-2 text-sm ml-6"
                  data-testid="press-test-email"
                />
              )}

              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={!testOnly}
                  onChange={() => setTestOnly(false)}
                  className="mt-1"
                  data-testid="press-mode-broadcast"
                />
                <div>
                  <p className="text-sm font-medium text-slate-900">Broadcast to matching journalists</p>
                  <p className="text-xs text-slate-500">Sends to every active contact whose country matches this release.</p>
                </div>
              </label>
            </div>

            {sendResult && (
              <div className={`border p-3 mb-4 text-xs ${sendResult.status === 'ok' ? 'bg-green-50 border-green-200 text-green-900' : 'bg-amber-50 border-amber-200 text-amber-900'}`}>
                <div className="flex items-center gap-2 font-bold mb-1">
                  {sendResult.status === 'ok'
                    ? <CheckCircle2 className="h-4 w-4 text-green-700" />
                    : <AlertTriangle className="h-4 w-4 text-amber-700" />}
                  {sendResult.status === 'ok' ? `Sent to ${sendResult.sent_count}` : sendResult.status}
                </div>
                {sendResult.failed_count > 0 && (
                  <p>{sendResult.failed_count} failed — check Resend dashboard for bounce reasons.</p>
                )}
                {sendResult.hint && <p>{sendResult.hint}</p>}
                {sendResult.sent?.slice(0, 5).map(e => (
                  <p key={e} className="font-mono">✓ {e}</p>
                ))}
                {sendResult.failed?.slice(0, 5).map(f => (
                  <p key={f.email} className="font-mono text-red-700"><XCircle className="h-3 w-3 inline mr-1" />{f.email}: {f.reason}</p>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <button
                onClick={() => setSendModal(null)}
                disabled={busy}
                className="px-3 py-2 text-xs uppercase tracking-wider border border-slate-300"
                data-testid="press-modal-close"
              >
                Close
              </button>
              <button
                disabled={busy}
                onClick={performSend}
                className="px-4 py-2 text-xs uppercase tracking-wider bg-[#E07A3A] text-white hover:bg-[#C9682E] disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="press-modal-send"
              >
                {busy ? <><Loader2 className="h-3 w-3 animate-spin" /> Sending...</> : <><Send className="h-3 w-3" /> Send now</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer privacy note */}
      <div className="text-xs text-slate-500 italic flex items-start gap-2">
        <Globe className="h-3 w-3 mt-0.5 flex-shrink-0" />
        <span>
          You are responsible for ensuring every contact on this list has been added with a legitimate basis (existing
          relationship, public press desk address, or opt-in). Bulk emailing scraped lists violates GDPR / UK PECR /
          Resend terms and will harm Shopperden's deliverability.
        </span>
      </div>
    </div>
  );
};
